import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Layout } from "@/components/Layout";
import { 
  Sliders, 
  Zap, 
  AlertTriangle, 
  CheckCircle2, 
  Download, 
  Radio,
  Info,
  Cpu,
  HelpCircle,
  Send,
  Loader2,
  Trash2,
  MapPin,
  Plus,
  Building2,
  Brain,
  Sparkles,
  Settings,
  X,
  Waves,
  Folder,
  FolderOpen,
  ChevronRight,
  ChevronDown
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { frequenciesAPI, systemsAPI, equipmentAPI, contactAPI, venuesAPI } from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { RFPredictionWidget, AIAnalysisDialog } from "@/components/RFPrediction";
import { UpgradeModal } from "@/components/UpgradeModal";
import { CascadingEquipmentSelector, BRAND_COLORS } from "@/components/frequencies";
import axios from "axios";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
});

// localStorage keys for persistence
const STORAGE_KEYS = {
  RESULT: 'avx_frequencies_result',
  FORM: 'avx_frequencies_form',
  EQUIPMENT_ID: 'avx_frequencies_equipment_id',
  BAND_ID: 'avx_frequencies_band_id',
  SYSTEM_PREFIX: 'avx_frequencies_prefix'
};

// Helper to safely parse JSON from localStorage
const getStoredValue = (key, defaultValue) => {
  try {
    const stored = localStorage.getItem(key);
    return stored ? JSON.parse(stored) : defaultValue;
  } catch {
    return defaultValue;
  }
};

export default function FrequenciesPage({ user, onLogout }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [systemPrefix, setSystemPrefix] = useState(() => 
    getStoredValue(STORAGE_KEYS.SYSTEM_PREFIX, "IEM")
  );
  const [result, setResult] = useState(() => 
    getStoredValue(STORAGE_KEYS.RESULT, null)
  );
  
  // Equipment state
  const [equipmentList, setEquipmentList] = useState([]);
  const [selectedEquipment, setSelectedEquipment] = useState(null);
  const [selectedEquipmentId, setSelectedEquipmentId] = useState(() => 
    getStoredValue(STORAGE_KEYS.EQUIPMENT_ID, "")
  );
  const [availableBands, setAvailableBands] = useState({});
  const [selectedBandId, setSelectedBandId] = useState(() => 
    getStoredValue(STORAGE_KEYS.BAND_ID, "")
  );
  
  // Contact form state
  const [showContactDialog, setShowContactDialog] = useState(false);
  const [sendingContact, setSendingContact] = useState(false);
  const [contactForm, setContactForm] = useState({
    name: "",
    email: "",
    equipment_brand: "",
    equipment_model: "",
    message: ""
  });
  
  // Save to Venue state
  const [showVenueDialog, setShowVenueDialog] = useState(false);
  const [venuesList, setVenuesList] = useState([]);
  const [loadingVenues, setLoadingVenues] = useState(false);
  const [savingToVenue, setSavingToVenue] = useState(false);
  const [selectedVenueId, setSelectedVenueId] = useState("");
  const [showNewVenueForm, setShowNewVenueForm] = useState(false);
  const [newVenueForm, setNewVenueForm] = useState({
    name: "",
    city: "",
    address: "",
    notes: ""
  });
  
  // AI Analysis state
  const [showAIAnalysis, setShowAIAnalysis] = useState(false);
  const [rfAnalysis, setRfAnalysis] = useState(null);
  
  // Spectrum RF Analysis for frequency assignment
  const [savedScans, setSavedScans] = useState([]);
  const [selectedScanIds, setSelectedScanIds] = useState([]);  // Multiple scan selection
  const [useSpectrumForAssignment, setUseSpectrumForAssignment] = useState(false);
  
  // Toggle scan selection for multi-select
  const toggleScanSelection = (scanId) => {
    setSelectedScanIds(prev => {
      if (prev.includes(scanId)) {
        return prev.filter(id => id !== scanId);
      } else {
        return [...prev, scanId];
      }
    });
  };
  
  // Calculate averaged spectrum info for display
  const getAveragedSpectrumInfo = () => {
    if (selectedScanIds.length === 0) return null;
    
    const selectedScansList = savedScans.filter(s => selectedScanIds.includes(s.id));
    if (selectedScansList.length === 0) return null;
    
    const allBandMins = selectedScansList.map(s => s.band_min);
    const allBandMaxs = selectedScansList.map(s => s.band_max);
    
    return {
      count: selectedScansList.length,
      bandMin: Math.min(...allBandMins),
      bandMax: Math.max(...allBandMaxs),
      names: selectedScansList.map(s => s.name)
    };
  };
  
  // Subscription state
  const [subscriptionUsage, setSubscriptionUsage] = useState(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  
  // Admin: Add Equipment state
  const [showAddEquipmentDialog, setShowAddEquipmentDialog] = useState(false);
  const [addingEquipment, setAddingEquipment] = useState(false);
  const [showPresetEditor, setShowPresetEditor] = useState(false);
  const [newEquipmentForm, setNewEquipmentForm] = useState({
    brand: "",
    model: "",
    band_name: "",
    band_min: "",
    band_max: "",
    system_type: "iem",
    max_systems: "16",
    channels_per_group: "6",
    notes: "",
    specifications: {
      min_spacing_mhz: "0.5",
      step_size_mhz: "0.5",
      snr_db: "90",
      power_mw: "10/30",
      modulation: "FM Estéreo"
    },
    preset_frequencies: {}
  });
  const [currentPresetGroup, setCurrentPresetGroup] = useState("A");
  const [presetGroupFrequencies, setPresetGroupFrequencies] = useState({
    1: "", 2: "", 3: "", 4: "", 5: "", 6: ""
  });
  
  const [form, setForm] = useState(() => 
    getStoredValue(STORAGE_KEYS.FORM, {
      num_channels: 6,
      band_min: 560,
      band_max: 674,
      min_spacing: 0.5,
    })
  );

  // Persist result to localStorage whenever it changes
  useEffect(() => {
    if (result) {
      localStorage.setItem(STORAGE_KEYS.RESULT, JSON.stringify(result));
    }
  }, [result]);

  // Persist form to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.FORM, JSON.stringify(form));
  }, [form]);

  // Persist equipment selection to localStorage and clear stale result
  useEffect(() => {
    if (selectedEquipmentId) {
      localStorage.setItem(STORAGE_KEYS.EQUIPMENT_ID, JSON.stringify(selectedEquipmentId));
      
      // Check if stored result belongs to a different equipment - if so, clear it
      const storedResult = getStoredValue(STORAGE_KEYS.RESULT, null);
      if (storedResult && storedResult.equipment_id && storedResult.equipment_id !== selectedEquipmentId) {
        // Clear stale result from different equipment
        localStorage.removeItem(STORAGE_KEYS.RESULT);
        setResult(null);
      }
    }
  }, [selectedEquipmentId]);

  // Persist band selection to localStorage
  useEffect(() => {
    if (selectedBandId) {
      localStorage.setItem(STORAGE_KEYS.BAND_ID, JSON.stringify(selectedBandId));
    }
  }, [selectedBandId]);

  // Persist system prefix to localStorage
  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.SYSTEM_PREFIX, JSON.stringify(systemPrefix));
  }, [systemPrefix]);

  // Fetch equipment list on mount
  useEffect(() => {
    const fetchEquipment = async () => {
      try {
        const response = await equipmentAPI.getAll();
        setEquipmentList(response.data.equipment || []);
        // Only set default if no equipment was previously selected (not from localStorage)
        const storedEquipmentId = getStoredValue(STORAGE_KEYS.EQUIPMENT_ID, null);
        if (!storedEquipmentId && response.data.equipment?.length > 0) {
          setSelectedEquipmentId("anleon_s3d");
        }
      } catch (error) {
        // Error handled silently
      }
    };
    
    const fetchSavedScans = async () => {
      try {
        const response = await axios.get(`${API_BASE}/scans`, getAuthHeaders());
        setSavedScans(response.data || []);
      } catch (error) {
        // Error handled silently
      }
    };
    
    fetchEquipment();
    fetchSavedScans();
    fetchSubscriptionUsage();
  }, []);

  const fetchSubscriptionUsage = async () => {
    try {
      const response = await axios.get(`${API_BASE}/subscriptions/usage`, getAuthHeaders());
      setSubscriptionUsage(response.data);
    } catch (error) {
      // Error handled silently
    }
  };

  // Admin: Add Equipment function
  const handleAddEquipment = async () => {
    if (!newEquipmentForm.brand || !newEquipmentForm.model || !newEquipmentForm.band_min || !newEquipmentForm.band_max) {
      toast.error("Completa todos los campos requeridos");
      return;
    }
    
    setAddingEquipment(true);
    try {
      // Build specifications object
      const specs = {
        min_spacing_mhz: parseFloat(newEquipmentForm.specifications.min_spacing_mhz) || 0.5,
        step_size_mhz: parseFloat(newEquipmentForm.specifications.step_size_mhz) || 0.5,
        snr_db: parseInt(newEquipmentForm.specifications.snr_db) || 90,
        power_mw: newEquipmentForm.specifications.power_mw.split("/").map(p => parseInt(p.trim())).filter(p => !isNaN(p)),
        modulation: newEquipmentForm.specifications.modulation || "FM Estéreo"
      };
      
      // Convert preset_frequencies to proper format (string keys to int for channels)
      const presetFreqs = Object.keys(newEquipmentForm.preset_frequencies).length > 0 
        ? newEquipmentForm.preset_frequencies 
        : null;
      
      const response = await axios.post(
        `${API_BASE}/equipment/admin/add`,
        {
          brand: newEquipmentForm.brand,
          model: newEquipmentForm.model,
          band_name: newEquipmentForm.band_name || "default",
          band_min: parseFloat(newEquipmentForm.band_min),
          band_max: parseFloat(newEquipmentForm.band_max),
          system_type: newEquipmentForm.system_type,
          max_systems: parseInt(newEquipmentForm.max_systems) || 16,
          channels_per_group: parseInt(newEquipmentForm.channels_per_group) || 6,
          notes: newEquipmentForm.notes || null,
          specifications: specs,
          preset_frequencies: presetFreqs
        },
        getAuthHeaders()
      );
      
      toast.success(response.data.message);
      setShowAddEquipmentDialog(false);
      setShowPresetEditor(false);
      setNewEquipmentForm({
        brand: "",
        model: "",
        band_name: "",
        band_min: "",
        band_max: "",
        system_type: "iem",
        max_systems: "16",
        channels_per_group: "6",
        notes: "",
        specifications: {
          min_spacing_mhz: "0.5",
          step_size_mhz: "0.5",
          snr_db: "90",
          power_mw: "10/30",
          modulation: "FM Estéreo"
        },
        preset_frequencies: {}
      });
      setCurrentPresetGroup("A");
      setPresetGroupFrequencies({ 1: "", 2: "", 3: "", 4: "", 5: "", 6: "" });
      
      // Refresh equipment list
      const eqResponse = await equipmentAPI.getAll();
      setEquipmentList(eqResponse.data.equipment || []);
    } catch (error) {
      const message = error.response?.data?.detail || "Error al agregar equipo";
      toast.error(message);
    } finally {
      setAddingEquipment(false);
    }
  };
  
  // Admin: Save preset group frequencies
  const handleSavePresetGroup = () => {
    const groupFreqs = {};
    let hasFrequencies = false;
    
    for (let ch = 1; ch <= parseInt(newEquipmentForm.channels_per_group) || 6; ch++) {
      const freq = parseFloat(presetGroupFrequencies[ch]);
      if (!isNaN(freq) && freq > 0) {
        groupFreqs[ch] = freq;
        hasFrequencies = true;
      }
    }
    
    if (hasFrequencies) {
      setNewEquipmentForm(prev => ({
        ...prev,
        preset_frequencies: {
          ...prev.preset_frequencies,
          [currentPresetGroup]: groupFreqs
        }
      }));
      toast.success(`Frecuencias del Grupo ${currentPresetGroup} guardadas`);
    } else {
      toast.error("Ingresa al menos una frecuencia válida");
    }
  };
  
  // Admin: Load preset group for editing
  const handleLoadPresetGroup = (group) => {
    setCurrentPresetGroup(group);
    const existingFreqs = newEquipmentForm.preset_frequencies[group] || {};
    const loadedFreqs = {};
    for (let ch = 1; ch <= 6; ch++) {
      loadedFreqs[ch] = existingFreqs[ch] ? existingFreqs[ch].toString() : "";
    }
    setPresetGroupFrequencies(loadedFreqs);
  };
  
  // Admin: Delete preset group
  const handleDeletePresetGroup = (group) => {
    const updated = { ...newEquipmentForm.preset_frequencies };
    delete updated[group];
    setNewEquipmentForm(prev => ({
      ...prev,
      preset_frequencies: updated
    }));
    toast.success(`Grupo ${group} eliminado`);
  };

  // Admin: Delete Equipment function
  const handleDeleteEquipment = async (equipmentId) => {
    if (!confirm("¿Estás seguro de eliminar este equipo?")) return;
    
    try {
      await axios.delete(`${API_BASE}/equipment/admin/${equipmentId}`, getAuthHeaders());
      toast.success("Equipo eliminado");
      
      // Refresh equipment list
      const eqResponse = await equipmentAPI.getAll();
      setEquipmentList(eqResponse.data.equipment || []);
    } catch (error) {
      toast.error("Error al eliminar equipo");
    }
  };

  // Fetch equipment details when selection changes
  useEffect(() => {
    const fetchEquipmentDetails = async () => {
      if (!selectedEquipmentId) return;
      
      try {
        const [detailsRes, bandsRes] = await Promise.all([
          equipmentAPI.getOne(selectedEquipmentId),
          equipmentAPI.getBands(selectedEquipmentId)
        ]);
        
        setSelectedEquipment(detailsRes.data);
        setAvailableBands(bandsRes.data.bands || {});
        
        const bandKeys = Object.keys(bandsRes.data.bands || {});
        
        // Auto-select first band and update form parameters
        if (bandKeys.length > 0) {
          const defaultBandId = bandKeys[0];
          setSelectedBandId(defaultBandId);
          
          const band = bandsRes.data.bands[defaultBandId];
          const specs = detailsRes.data.specifications || {};
          
          // Get min/max freq supporting both formats
          const bandMin = band.min_freq || band.freq_min;
          const bandMax = band.max_freq || band.freq_max;
          
          // AUTO-UPDATE all parameters based on equipment specs
          const newForm = {
            num_channels: Math.min(form.num_channels || 6, Math.min(specs.max_systems_recommended || 8, 8)),
            band_min: bandMin,
            band_max: bandMax,
            min_spacing: specs.min_spacing_mhz || 0.5,
          };
          setForm(newForm);
          
          // AUTO-CALCULATE frequencies with new parameters
          try {
            const response = await equipmentAPI.assignFrequencies({
              equipment_id: selectedEquipmentId,
              band_id: defaultBandId,
              num_channels: newForm.num_channels,
              custom_band_min: newForm.band_min,
              custom_band_max: newForm.band_max
            });
            
            const transformedResult = {
              total_channels: response.data.total_channels,
              spacing: response.data.actual_spacing,
              band_range: response.data.band_range,
              assigned_frequencies: response.data.assigned_frequencies,
              equipment: response.data.equipment,
              equipment_id: response.data.equipment_id,
              recommendations: response.data.recommendations,
              warning: response.data.warning
            };
            
            setResult(transformedResult);
          } catch (calcError) {
            // Error handled silently
          }
        }
        
        // Update system prefix based on brand
        const brandShort = detailsRes.data.brand?.substring(0, 3).toUpperCase() || "IEM";
        setSystemPrefix(brandShort);
        
      } catch (error) {
        // Error handled silently
      }
    };
    
    fetchEquipmentDetails();
  }, [selectedEquipmentId]);

  // Update band range when band selection changes and auto-calculate
  // Only run this effect when band changes after equipment is already loaded
  useEffect(() => {
    const updateBandAndCalculate = async () => {
      // Skip if this is being triggered by equipment change (which handles its own calculation)
      if (!selectedBandId || !availableBands[selectedBandId] || !selectedEquipmentId) return;
      
      const band = availableBands[selectedBandId];
      
      // Get min/max supporting both formats
      const bandMin = band.min_freq || band.freq_min;
      const bandMax = band.max_freq || band.freq_max;
      
      // Check if form already has these values (to avoid duplicate calculations)
      if (form.band_min === bandMin && form.band_max === bandMax) {
        return;
      }
      
      const newForm = {
        ...form,
        band_min: bandMin,
        band_max: bandMax
      };
      setForm(newForm);
      
      // AUTO-CALCULATE frequencies with new band
      try {
        const response = await equipmentAPI.assignFrequencies({
          equipment_id: selectedEquipmentId,
          band_id: selectedBandId,
          num_channels: newForm.num_channels,
          custom_band_min: newForm.band_min,
          custom_band_max: newForm.band_max
        });
        
        const transformedResult = {
          total_channels: response.data.total_channels,
          spacing: response.data.actual_spacing,
          band_range: response.data.band_range,
          assigned_frequencies: response.data.assigned_frequencies,
          equipment: response.data.equipment,
          equipment_id: response.data.equipment_id,
          recommendations: response.data.recommendations,
          warning: response.data.warning
        };
        
        setResult(transformedResult);
      } catch (calcError) {
        // Error handled silently
      }
    };
    
    updateBandAndCalculate();
  }, [selectedBandId, availableBands]);

  const handleAssign = async () => {
    setLoading(true);
    try {
      // Use equipment-specific endpoint if equipment is selected
      if (selectedEquipmentId) {
        const requestData = {
          equipment_id: selectedEquipmentId,
          band_id: selectedBandId || "default",
          num_channels: form.num_channels,
          custom_band_min: form.band_min,
          custom_band_max: form.band_max
        };
        
        // Add spectrum analysis if enabled (with multiple scans support)
        if (useSpectrumForAssignment && selectedScanIds.length > 0) {
          requestData.scan_ids = selectedScanIds;  // Send array of scan IDs
          requestData.noise_threshold_db = -70;
        }
        
        const response = await equipmentAPI.assignFrequencies(requestData);
        
        // Transform response to match expected format
        const transformedResult = {
          total_channels: response.data.total_channels,
          spacing: response.data.actual_spacing,
          band_range: response.data.band_range,
          assigned_frequencies: response.data.assigned_frequencies,
          equipment: response.data.equipment,
          equipment_id: response.data.equipment_id,
          recommendations: response.data.recommendations,
          warning: response.data.warning,
          using_spectrum: response.data.using_spectrum,
          using_presets: response.data.using_presets
        };
        
        setResult(transformedResult);
        
        if (response.data.warning) {
          toast.warning(response.data.warning);
        }
        
        const spectrumMsg = response.data.using_spectrum 
          ? ` (promedio de ${selectedScanIds.length} escaneo${selectedScanIds.length > 1 ? 's' : ''} RF)` 
          : "";
        const presetMsg = response.data.using_presets ? " usando presets" : "";
        toast.success(
          `${response.data.total_channels} frecuencias asignadas${presetMsg}${spectrumMsg}`
        );
      } else {
        // Fallback to generic endpoint
        const response = await frequenciesAPI.assign(form);
        setResult(response.data);
        toast.success(
          `${response.data.total_channels} frecuencias asignadas exitosamente`
        );
      }
    } catch (error) {
      toast.error(
        error.response?.data?.detail || "Error al calcular frecuencias"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleImportToSystems = async () => {
    if (!result || !result.assigned_frequencies) return;
    
    // Verify result belongs to currently selected equipment
    if (result.equipment_id && result.equipment_id !== selectedEquipmentId) {
      toast.error("El resultado pertenece a un equipo diferente. Por favor recalcula las frecuencias.");
      return;
    }
    
    setImporting(true);
    try {
      let successCount = 0;
      let errorCount = 0;
      
      for (const freq of result.assigned_frequencies) {
        try {
          // Determine system type based on equipment name
          let systemType = "iem"; // default
          const eqName = (selectedEquipment?.full_name || selectedEquipment?.name || "").toLowerCase();
          const eqBrand = (selectedEquipment?.brand || "").toUpperCase();
          
          // Shure wireless mics: QLXD, ULXD, SLX, BLX (not PSM which is IEM)
          const isMicrophonePattern = /\b(qlx|ulx|slx|blx|pgx|sm58|sm87|beta|ksm|ew|skm|ek|sr\s?2050|em\s?2050)\b/i;
          const isIEMPattern = /\b(psm|iem|in.?ear|monitor|sr20|s3d)\b/i;
          
          if (isMicrophonePattern.test(eqName) && !isIEMPattern.test(eqName)) {
            systemType = "microphone";
          }
          
          // Determine default channel group based on brand
          // Sennheiser uses numeric groups (1, 2, 3...), Shure uses alphabetic (A, B, C...)
          let defaultGroup = "A";
          if (eqBrand.includes("SENNHEISER") || eqName.includes("sennheiser") || 
              eqName.includes("ew ") || eqName.includes("ew-") || eqName.includes("em 2050")) {
            defaultGroup = "1";
          } else if (eqBrand.includes("AUDIO-TECHNICA") || eqName.includes("audio-technica")) {
            defaultGroup = "1";
          }
          
          const systemData = {
            name: `${systemPrefix} ${freq.channel}`,
            equipment_id: selectedEquipmentId || "anleon_s3d",
            frequency: freq.frequency,
            channel_group: freq.group || freq.channel_group || defaultGroup,
            channel_number: freq.channel_number || freq.channel || 1,
            band_id: selectedBandId || "default",
            band_min: form.band_min,
            band_max: form.band_max,
            system_type: systemType
          };
          
          // Debug log removed // Debug log
          
          await systemsAPI.create(systemData);
          successCount++;
        } catch (err) {
          // Check if it's a subscription limit error
          const errorDetail = err.response?.data?.detail;
          if (errorDetail?.error === "subscription_limit") {
            setShowImportDialog(false);
            setShowUpgradeModal(true);
            toast.error(errorDetail.message || "Has alcanzado el límite de sistemas de tu plan");
            setImporting(false);
            return; // Stop trying to create more systems
          }
          errorCount++;
          const errorMsg = err.response?.data?.detail || err.message;
          // Error handled silently
          
          // If first error is equipment not found, show specific message and stop
          if (errorCount === 1 && err.response?.status === 404) {
            setShowImportDialog(false);
            toast.error(errorMsg);
            setImporting(false);
            return; // Stop trying to create more systems
          }
        }
      }
      
      if (successCount > 0) {
        toast.success(`${successCount} sistemas ${selectedEquipment?.full_name || "In-Ear"} creados exitosamente`);
        setShowImportDialog(false);
        
        // Clear stored result after successful import
        clearStoredResult();
        fetchSubscriptionUsage(); // Refresh usage
        
        // Navigate to systems page
        setTimeout(() => {
          navigate("/systems");
        }, 1000);
      }
      
      if (errorCount > 0 && successCount === 0) {
        toast.error(`${errorCount} sistemas no pudieron ser creados. Verifica que el equipo esté importado correctamente.`);
      } else if (errorCount > 0) {
        toast.warning(`${successCount} sistemas creados, ${errorCount} fallaron`);
      }
      
    } catch (error) {
      toast.error("Error al importar sistemas");
    } finally {
      setImporting(false);
    }
  };

  // Clear stored calculation result
  const clearStoredResult = () => {
    setResult(null);
    localStorage.removeItem(STORAGE_KEYS.RESULT);
  };

  // Fetch venues when dialog opens
  const handleOpenVenueDialog = async () => {
    setShowVenueDialog(true);
    setLoadingVenues(true);
    setSelectedVenueId("");
    setShowNewVenueForm(false);
    
    try {
      const response = await venuesAPI.getAll();
      setVenuesList(response.data.venues || []);
    } catch (error) {
      // Error handled silently
      toast.error("Error al cargar los venues");
    } finally {
      setLoadingVenues(false);
    }
  };

  // Save frequencies to existing venue
  const handleSaveToExistingVenue = async () => {
    if (!selectedVenueId || !result) return;
    
    setSavingToVenue(true);
    try {
      const frequencies = result.assigned_frequencies.map(freq => ({
        channel: freq.channel,
        frequency: freq.frequency,
        system_name: `${systemPrefix} ${freq.channel}`
      }));
      
      await venuesAPI.saveFrequencies(selectedVenueId, frequencies);
      
      const selectedVenue = venuesList.find(v => v.id === selectedVenueId);
      toast.success(`Frecuencias guardadas en "${selectedVenue?.name}"`);
      setShowVenueDialog(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al guardar frecuencias");
    } finally {
      setSavingToVenue(false);
    }
  };

  // Create new venue and save frequencies
  const handleCreateVenueAndSave = async () => {
    if (!newVenueForm.name || !newVenueForm.city || !result) {
      toast.error("Por favor completa nombre y ciudad del venue");
      return;
    }
    
    setSavingToVenue(true);
    try {
      // Prepare frequencies
      const frequencies = result.assigned_frequencies.map(freq => ({
        channel: freq.channel,
        frequency: freq.frequency,
        system_name: `${systemPrefix} ${freq.channel}`
      }));
      
      // Create venue with frequencies
      const venueData = {
        name: newVenueForm.name,
        city: newVenueForm.city,
        address: newVenueForm.address || "",
        notes: newVenueForm.notes || "",
        equipment_id: selectedEquipmentId,
        frequencies: frequencies
      };
      
      const response = await venuesAPI.create(venueData);
      toast.success(`Venue "${newVenueForm.name}" creado con ${frequencies.length} frecuencias`);
      setShowVenueDialog(false);
      setNewVenueForm({ name: "", city: "", address: "", notes: "" });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al crear venue");
    } finally {
      setSavingToVenue(false);
    }
  };

  // Handle contact form submission
  const handleContactSubmit = async () => {
    // Validate form
    if (!contactForm.name || !contactForm.email || !contactForm.equipment_brand || !contactForm.equipment_model) {
      toast.error("Por favor completa todos los campos requeridos");
      return;
    }

    setSendingContact(true);
    try {
      const response = await contactAPI.submitEquipmentRequest(contactForm);
      toast.success(response.data.message || "¡Solicitud enviada!");
      setShowContactDialog(false);
      setContactForm({
        name: "",
        email: "",
        equipment_brand: "",
        equipment_model: "",
        message: ""
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al enviar la solicitud");
    } finally {
      setSendingContact(false);
    }
  };

  // Get max systems for selected equipment
  // Maximum 8 systems at a time
  const maxSystems = Math.min(selectedEquipment?.specifications?.max_systems_recommended || 8, 8);

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="top-bar">
        <div>
          <h1 className="font-heading text-xl font-bold text-[#F4F4F5]">
            Asignación de Frecuencias
          </h1>
          <p className="text-sm text-[#71717A]">
            Cálculo inteligente con prevención de intermodulación
          </p>
        </div>
      </div>

      {/* Equipment Selection Cards - Grouped by Brand */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-medium text-[#71717A]">Seleccionar Equipo IEM</h2>
          {/* Admin: Add Equipment Button */}
          {user?.role === "admin" && (
            <Button
              onClick={() => setShowAddEquipmentDialog(true)}
              size="sm"
              className="bg-[#34C759] hover:bg-[#34C759]/90 text-black"
            >
              <Plus className="w-4 h-4 mr-1" />
              Agregar Equipo
            </Button>
          )}
        </div>
        
        {/* NEW: Cascading Equipment Selector */}
        <CascadingEquipmentSelector
          equipmentList={equipmentList}
          selectedEquipmentId={selectedEquipmentId}
          selectedBandId={selectedBandId}
          onSelectEquipment={setSelectedEquipmentId}
          onSelectBand={setSelectedBandId}
          availableBands={availableBands}
        />
        
        {/* "Don't see your equipment?" link */}
        <div className="mt-3 flex items-center gap-2 text-sm">
          <HelpCircle className="w-4 h-4 text-[#71717A]" />
          <span className="text-[#71717A]">¿No ves tu equipo?</span>
          <button
            onClick={() => setShowContactDialog(true)}
            className="text-[#34C759] hover:text-[#34C759]/80 underline underline-offset-2 transition-colors"
            data-testid="request-equipment-btn"
          >
            Contáctanos para agregarlo
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Configuration Panel */}
        <div className="bg-[#111111] border border-white/[0.08] rounded-sm p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-heading font-semibold text-[#F4F4F5]">
              Parámetros de Asignación
            </h2>
            {selectedEquipment && (
              <span className="px-2 py-1 text-xs bg-[#34C759]/20 text-[#34C759] rounded-sm">
                {selectedEquipment.full_name}
              </span>
            )}
          </div>

          <div className="space-y-6">
            <div className="form-group">
              <div className="flex items-center justify-between mb-2">
                <Label className="form-label mb-0">Cantidad de Sistemas</Label>
                <span className="font-mono text-lg text-[#34C759]">
                  {form.num_channels}
                </span>
              </div>
              <Slider
                value={[form.num_channels]}
                onValueChange={(v) =>
                  setForm({ ...form, num_channels: v[0] })
                }
                min={1}
                max={Math.min(maxSystems * 2, 32)}
                step={1}
                className="py-4"
                data-testid="channels-slider"
              />
              <div className="flex justify-between text-xs text-[#71717A]">
                <span>1</span>
                <span className={form.num_channels > maxSystems ? "text-[#FFCC00]" : ""}>
                  Recomendado: {maxSystems}
                </span>
                <span>{Math.min(maxSystems * 2, 32)}</span>
              </div>
              {form.num_channels > maxSystems && (
                <p className="text-xs text-[#FFCC00] mt-2 flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" />
                  Excede el máximo recomendado para {selectedEquipment?.model}
                </p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="form-group">
                <Label className="form-label">Banda Mín (MHz)</Label>
                <Input
                  type="number"
                  value={form.band_min}
                  onChange={(e) =>
                    setForm({ ...form, band_min: parseFloat(e.target.value) })
                  }
                  className="input-tactical font-mono"
                  data-testid="band-min-input"
                />
              </div>
              <div className="form-group">
                <Label className="form-label">Banda Máx (MHz)</Label>
                <Input
                  type="number"
                  value={form.band_max}
                  onChange={(e) =>
                    setForm({ ...form, band_max: parseFloat(e.target.value) })
                  }
                  className="input-tactical font-mono"
                  data-testid="band-max-input"
                />
              </div>
            </div>

            <div className="form-group">
              <div className="flex items-center justify-between mb-2">
                <Label className="form-label mb-0">Espaciado Mínimo (MHz)</Label>
                <span className="font-mono text-[#34C759]">
                  {form.min_spacing.toFixed(3)} MHz
                </span>
              </div>
              <Slider
                value={[form.min_spacing * 1000]}
                onValueChange={(v) =>
                  setForm({ ...form, min_spacing: v[0] / 1000 })
                }
                min={25}
                max={2000}
                step={25}
                className="py-4"
                data-testid="spacing-slider"
              />
              <div className="flex justify-between text-xs text-[#71717A]">
                <span>0.025 MHz</span>
                <span>2.000 MHz</span>
              </div>
            </div>

            {/* Equipment Specs Info */}
            {selectedEquipment && (
              <div className="p-3 bg-[#0A0A0A] border border-white/[0.05] rounded-sm">
                <div className="flex items-center gap-2 mb-2">
                  <Info className="w-4 h-4 text-[#3B82F6]" />
                  <span className="text-xs font-medium text-[#3B82F6]">
                    Especificaciones {selectedEquipment.brand}
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div>
                    <span className="text-[#71717A]">Espaciado mín:</span>
                    <span className="text-[#F4F4F5] ml-1">
                      {selectedEquipment.specifications?.min_spacing_mhz} MHz
                    </span>
                  </div>
                  <div>
                    <span className="text-[#71717A]">SNR:</span>
                    <span className="text-[#F4F4F5] ml-1">
                      {selectedEquipment.specifications?.snr_db} dB
                    </span>
                  </div>
                  <div>
                    <span className="text-[#71717A]">Modulación:</span>
                    <span className="text-[#F4F4F5] ml-1">
                      {selectedEquipment.specifications?.modulation}
                    </span>
                  </div>
                  <div>
                    <span className="text-[#71717A]">Potencia:</span>
                    <span className="text-[#F4F4F5] ml-1">
                      {selectedEquipment.specifications?.power_mw?.join("/")} mW
                    </span>
                  </div>
                </div>
              </div>
            )}

            <div className="pt-4 border-t border-white/[0.08]">
              {/* Spectrum RF Analysis Option - Multiple Selection */}
              {savedScans.length > 0 && (
                <div className="mb-4 p-3 bg-[#34C759]/10 border border-[#34C759]/30 rounded-sm">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Waves className="w-4 h-4 text-[#34C759]" />
                      <span className="text-sm font-medium text-[#34C759]">
                        Usar Espectro RF (Promedio)
                      </span>
                    </div>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={useSpectrumForAssignment}
                        onChange={(e) => {
                          setUseSpectrumForAssignment(e.target.checked);
                          if (!e.target.checked) setSelectedScanIds([]);
                        }}
                        className="w-4 h-4 rounded border-white/20 bg-[#0A0A0A] text-[#34C759] focus:ring-[#34C759]"
                      />
                      <span className="text-xs text-[#A1A1AA]">Activar</span>
                    </label>
                  </div>
                  
                  {useSpectrumForAssignment && (
                    <div className="mt-2 space-y-2">
                      <p className="text-xs text-[#A1A1AA] mb-2">
                        Selecciona uno o más escaneos para promediar:
                      </p>
                      <div className="max-h-40 overflow-y-auto space-y-1 pr-1">
                        {savedScans.map((scan) => (
                          <label 
                            key={scan.id} 
                            className={`flex items-center gap-2 p-2 rounded-sm cursor-pointer transition-all ${
                              selectedScanIds.includes(scan.id) 
                                ? 'bg-[#34C759]/20 border border-[#34C759]/50' 
                                : 'bg-[#0A0A0A] border border-white/[0.08] hover:border-white/20'
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={selectedScanIds.includes(scan.id)}
                              onChange={() => toggleScanSelection(scan.id)}
                              className="w-4 h-4 rounded border-white/20 bg-[#0A0A0A] text-[#34C759] focus:ring-[#34C759]"
                            />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm text-[#F4F4F5] truncate">{scan.name}</p>
                              <p className="text-xs text-[#71717A]">
                                {scan.band_min}-{scan.band_max} MHz • {scan.scan_data?.length || 0} puntos
                              </p>
                            </div>
                          </label>
                        ))}
                      </div>
                      
                      {selectedScanIds.length > 0 && (
                        <div className="mt-3 p-2 bg-[#0A0A0A] rounded-sm border border-[#34C759]/30">
                          <div className="flex items-center gap-2 mb-1">
                            <Brain className="w-4 h-4 text-[#34C759]" />
                            <span className="text-xs font-medium text-[#34C759]">
                              Promedio de {selectedScanIds.length} escaneo{selectedScanIds.length > 1 ? 's' : ''}
                            </span>
                          </div>
                          {getAveragedSpectrumInfo() && (
                            <p className="text-xs text-[#71717A]">
                              Rango combinado: {getAveragedSpectrumInfo().bandMin.toFixed(0)}-{getAveragedSpectrumInfo().bandMax.toFixed(0)} MHz
                            </p>
                          )}
                        </div>
                      )}
                      
                      <p className="text-xs text-[#71717A] mt-2">
                        Las frecuencias se asignarán evitando zonas con interferencia promedio detectada
                      </p>
                    </div>
                  )}
                </div>
              )}

              <div className="flex items-start gap-3 mb-4 p-3 bg-[#FFCC00]/10 border border-[#FFCC00]/30 rounded-sm">
                <AlertTriangle className="w-4 h-4 text-[#FFCC00] mt-0.5 flex-shrink-0" />
                <div className="text-xs text-[#A1A1AA]">
                  <p className="font-medium text-[#FFCC00] mb-1">
                    Prevención de Intermodulación
                  </p>
                  El algoritmo calcula frecuencias con separación óptima para
                  evitar productos de intermodulación entre canales.
                </div>
              </div>

              <Button
                onClick={handleAssign}
                disabled={loading || !selectedEquipmentId}
                className="btn-tactical btn-tactical-primary w-full"
                data-testid="calculate-frequencies-button"
              >
                {loading ? (
                  "Calculando..."
                ) : (
                  <>
                    <Zap className="w-4 h-4 mr-2" />
                    Calcular {form.num_channels} Frecuencias {useSpectrumForAssignment && selectedScanIds.length > 0 ? `(+ ${selectedScanIds.length} Espectro${selectedScanIds.length > 1 ? 's' : ''})` : ""}
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>

        {/* Results Panel */}
        <div className="bg-[#111111] border border-white/[0.08] rounded-sm p-6">
          <h2 className="font-heading font-semibold text-[#F4F4F5] mb-6">
            Frecuencias Asignadas
          </h2>

          {!result ? (
            <div className="text-center py-16">
              <Sliders className="w-16 h-16 mx-auto mb-4 text-[#71717A] opacity-30" />
              <p className="text-[#71717A]">
                Selecciona un equipo y calcula las frecuencias óptimas
              </p>
            </div>
          ) : (
            <div data-testid="frequencies-result">
              {/* Equipment Badge */}
              {result.equipment && (
                <div className="mb-4 p-3 bg-[#34C759]/10 border border-[#34C759]/30 rounded-sm">
                  <div className="flex items-center gap-2">
                    <Cpu className="w-4 h-4 text-[#34C759]" />
                    <span className="text-sm font-medium text-[#34C759]">
                      {result.equipment}
                    </span>
                  </div>
                </div>
              )}

              {/* Summary */}
              <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="bg-[#0A0A0A] border border-white/[0.08] rounded-sm p-3 text-center">
                  <p className="text-xs text-[#71717A] mb-1">Sistemas</p>
                  <p className="font-mono text-xl text-[#34C759]">
                    {result.total_channels}
                  </p>
                </div>
                <div className="bg-[#0A0A0A] border border-white/[0.08] rounded-sm p-3 text-center">
                  <p className="text-xs text-[#71717A] mb-1">Espaciado</p>
                  <p className="font-mono text-xl text-[#F4F4F5]">
                    {result.spacing} MHz
                  </p>
                </div>
                <div className="bg-[#0A0A0A] border border-white/[0.08] rounded-sm p-3 text-center">
                  <p className="text-xs text-[#71717A] mb-1">Banda</p>
                  <p className="font-mono text-sm text-[#F4F4F5]">
                    {result.band_range}
                  </p>
                </div>
              </div>

              {/* Warning */}
              {result.warning && (
                <div className="mb-4 p-3 bg-[#FFCC00]/10 border border-[#FFCC00]/30 rounded-sm">
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-[#FFCC00]" />
                    <span className="text-sm text-[#FFCC00]">{result.warning}</span>
                  </div>
                </div>
              )}

              {/* Frequency List */}
              <div className="space-y-2 max-h-80 overflow-y-auto pr-2">
                {result.assigned_frequencies.map((freq, idx) => (
                  <div
                    key={idx}
                    className="flex items-center justify-between p-3 bg-[#0A0A0A] border border-white/[0.08] rounded-sm hover:border-[#34C759]/30 transition-colors"
                    data-testid={`frequency-item-${freq.channel}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-sm bg-[#34C759]/10 flex items-center justify-center">
                        <span className="font-mono text-xs text-[#34C759]">
                          {freq.channel_label || freq.channel}
                        </span>
                      </div>
                      <div>
                        <p className="font-mono text-lg text-[#F4F4F5]">
                          {freq.frequency.toFixed(3)}{" "}
                          <span className="text-xs text-[#71717A]">MHz</span>
                        </p>
                        {freq.suggested_spacing && (
                          <p className="text-xs text-[#71717A]">
                            Espaciado: {freq.suggested_spacing} MHz
                          </p>
                        )}
                      </div>
                    </div>
                    <CheckCircle2 className="w-4 h-4 text-[#34C759]" />
                  </div>
                ))}
              </div>

              {/* Recommendations */}
              {result.recommendations && result.recommendations.length > 0 && (
                <div className="mt-4 p-3 bg-[#0A0A0A] border border-white/[0.05] rounded-sm">
                  <p className="text-xs font-medium text-[#71717A] mb-2">Recomendaciones:</p>
                  <ul className="space-y-1">
                    {result.recommendations.map((rec, idx) => (
                      <li key={idx} className="text-xs text-[#A1A1AA] flex items-start gap-2">
                        <span className="text-[#34C759]">•</span>
                        {rec}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="mt-4 pt-4 border-t border-white/[0.08] space-y-3">
                {/* Import Button */}
                <Button
                  onClick={() => setShowImportDialog(true)}
                  className="w-full bg-green-600 hover:bg-green-700 text-white"
                  data-testid="import-to-systems-btn"
                >
                  <Radio className="w-4 h-4 mr-2" />
                  Crear {result.total_channels} Sistemas {selectedEquipment?.brand || "In-Ear"}
                </Button>
                
                {/* Save to Venue Button */}
                <Button
                  onClick={handleOpenVenueDialog}
                  variant="outline"
                  className="w-full border-[#34C759]/50 text-[#34C759] hover:bg-[#34C759]/10 hover:border-[#34C759]"
                  data-testid="save-to-venue-btn"
                >
                  <MapPin className="w-4 h-4 mr-2" />
                  Guardar en Venue
                </Button>
                
                {/* AI Analysis Section */}
                <div className="pt-2 border-t border-white/[0.06]">
                  <RFPredictionWidget
                    frequencies={result.assigned_frequencies?.map(f => f.frequency) || []}
                    equipmentName={selectedEquipment?.full_name}
                    onAnalysisComplete={setRfAnalysis}
                  />
                  
                  {rfAnalysis && (
                    <Button
                      onClick={() => setShowAIAnalysis(true)}
                      variant="ghost"
                      className="w-full mt-2 text-[#8B5CF6] hover:text-[#8B5CF6] hover:bg-[#8B5CF6]/10"
                      data-testid="detailed-ai-analysis-btn"
                    >
                      <Sparkles className="w-4 h-4 mr-2" />
                      Análisis Detallado con IA
                    </Button>
                  )}
                </div>
                
                {/* Clear Button */}
                <Button
                  onClick={clearStoredResult}
                  variant="outline"
                  className="w-full border-white/20 text-[#A1A1AA] hover:text-white hover:border-white/40"
                  data-testid="clear-calculation-btn"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Limpiar Cálculo
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Import Dialog */}
      <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
        <DialogContent className="bg-[#111111] border-white/[0.08]">
          <DialogHeader>
            <DialogTitle className="text-white">Importar a Sistemas In-Ear</DialogTitle>
            <DialogDescription className="text-gray-400">
              Se crearán {result?.total_channels || 0} sistemas {selectedEquipment?.full_name || "In-Ear"} con las frecuencias calculadas
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-gray-300">Prefijo del nombre</Label>
              <Input
                value={systemPrefix}
                onChange={(e) => setSystemPrefix(e.target.value)}
                placeholder="Ej: IEM, Voz, Monitor"
                className="bg-[#0A0A0A] border-white/[0.08] text-white"
              />
              <p className="text-xs text-gray-500">
                Los sistemas se nombrarán: {systemPrefix} 1, {systemPrefix} 2, etc.
              </p>
            </div>

            {selectedEquipment && (
              <div className="p-3 bg-[#34C759]/10 border border-[#34C759]/30 rounded-sm">
                <div className="flex items-center gap-2">
                  <Cpu className="w-4 h-4 text-[#34C759]" />
                  <span className="text-sm text-[#34C759]">
                    Equipo: {selectedEquipment.full_name}
                  </span>
                </div>
              </div>
            )}
            
            <div className="bg-[#0A0A0A] border border-white/[0.08] rounded-sm p-4 max-h-48 overflow-y-auto">
              <p className="text-xs text-gray-500 mb-2">Vista previa:</p>
              {result?.assigned_frequencies?.map((freq, idx) => (
                <div key={idx} className="flex justify-between text-sm py-1 border-b border-white/[0.04] last:border-0">
                  <span className="text-white">{systemPrefix} {freq.channel}</span>
                  <span className="text-green-400 font-mono">{freq.frequency.toFixed(3)} MHz</span>
                </div>
              ))}
            </div>
          </div>
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowImportDialog(false)}
              className="border-gray-600 text-gray-300"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleImportToSystems}
              disabled={importing}
              className="bg-green-600 hover:bg-green-700"
            >
              {importing ? (
                "Creando sistemas..."
              ) : (
                <>
                  <Download className="w-4 h-4 mr-2" />
                  Crear {result?.total_channels || 0} Sistemas
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Contact Dialog - Request New Equipment */}
      <Dialog open={showContactDialog} onOpenChange={setShowContactDialog}>
        <DialogContent className="bg-[#111111] border-white/10 sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="text-[#F4F4F5] flex items-center gap-2">
              <Send className="w-5 h-5 text-[#34C759]" />
              Solicitar Nuevo Equipo
            </DialogTitle>
            <DialogDescription className="text-[#71717A]">
              Completa el formulario y nos pondremos en contacto para agregar tu equipo a la plataforma.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="form-group">
                <Label className="form-label">
                  Tu Nombre <span className="text-red-500">*</span>
                </Label>
                <Input
                  value={contactForm.name}
                  onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                  placeholder="Juan Pérez"
                  className="input-tactical"
                  data-testid="contact-name-input"
                />
              </div>
              <div className="form-group">
                <Label className="form-label">
                  Email <span className="text-red-500">*</span>
                </Label>
                <Input
                  type="email"
                  value={contactForm.email}
                  onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                  placeholder="tu@email.com"
                  className="input-tactical"
                  data-testid="contact-email-input"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="form-group">
                <Label className="form-label">
                  Marca del Equipo <span className="text-red-500">*</span>
                </Label>
                <Input
                  value={contactForm.equipment_brand}
                  onChange={(e) => setContactForm({ ...contactForm, equipment_brand: e.target.value })}
                  placeholder="Ej: Behringer, Line 6..."
                  className="input-tactical"
                  data-testid="contact-brand-input"
                />
              </div>
              <div className="form-group">
                <Label className="form-label">
                  Modelo <span className="text-red-500">*</span>
                </Label>
                <Input
                  value={contactForm.equipment_model}
                  onChange={(e) => setContactForm({ ...contactForm, equipment_model: e.target.value })}
                  placeholder="Ej: P2-S, XD-V75..."
                  className="input-tactical"
                  data-testid="contact-model-input"
                />
              </div>
            </div>
            
            <div className="form-group">
              <Label className="form-label">
                Mensaje Adicional (opcional)
              </Label>
              <Textarea
                value={contactForm.message}
                onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                placeholder="Detalles adicionales sobre el equipo, bandas de frecuencia, etc..."
                className="input-tactical min-h-[80px] resize-none"
                data-testid="contact-message-input"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowContactDialog(false)}
              className="border-gray-600 text-gray-300"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleContactSubmit}
              disabled={sendingContact}
              className="bg-[#34C759] hover:bg-[#34C759]/90 text-black"
              data-testid="contact-submit-btn"
            >
              {sendingContact ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Enviando...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Enviar Solicitud
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Save to Venue Dialog */}
      <Dialog open={showVenueDialog} onOpenChange={setShowVenueDialog}>
        <DialogContent className="bg-[#111111] border-white/10 sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="text-[#F4F4F5] flex items-center gap-2">
              <MapPin className="w-5 h-5 text-[#34C759]" />
              Guardar en Venue
            </DialogTitle>
            <DialogDescription className="text-[#71717A]">
              Guarda estas {result?.total_channels || 0} frecuencias en un venue para usarlas rápidamente en futuros eventos.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {loadingVenues ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 text-[#34C759] animate-spin" />
              </div>
            ) : (
              <>
                {/* Toggle between existing and new venue */}
                <div className="flex gap-2 mb-4">
                  <Button
                    variant={!showNewVenueForm ? "default" : "outline"}
                    onClick={() => setShowNewVenueForm(false)}
                    className={!showNewVenueForm 
                      ? "flex-1 bg-[#34C759] hover:bg-[#34C759]/90 text-black" 
                      : "flex-1 border-white/20 text-[#A1A1AA]"
                    }
                    data-testid="venue-existing-tab"
                  >
                    <Building2 className="w-4 h-4 mr-2" />
                    Venue Existente
                  </Button>
                  <Button
                    variant={showNewVenueForm ? "default" : "outline"}
                    onClick={() => setShowNewVenueForm(true)}
                    className={showNewVenueForm 
                      ? "flex-1 bg-[#34C759] hover:bg-[#34C759]/90 text-black" 
                      : "flex-1 border-white/20 text-[#A1A1AA]"
                    }
                    data-testid="venue-new-tab"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Crear Nuevo
                  </Button>
                </div>

                {!showNewVenueForm ? (
                  // Select existing venue
                  <div className="space-y-4">
                    {venuesList.length === 0 ? (
                      <div className="text-center py-6 border border-dashed border-white/20 rounded-sm">
                        <Building2 className="w-10 h-10 mx-auto text-[#71717A] mb-2" />
                        <p className="text-[#71717A] text-sm">No tienes venues guardados</p>
                        <Button
                          variant="link"
                          onClick={() => setShowNewVenueForm(true)}
                          className="text-[#34C759] mt-2"
                        >
                          Crear tu primer venue
                        </Button>
                      </div>
                    ) : (
                      <>
                        <div className="form-group">
                          <Label className="form-label">Seleccionar Venue</Label>
                          <Select
                            value={selectedVenueId}
                            onValueChange={setSelectedVenueId}
                          >
                            <SelectTrigger className="input-tactical" data-testid="venue-select">
                              <SelectValue placeholder="Selecciona un venue..." />
                            </SelectTrigger>
                            <SelectContent className="bg-[#1A1A1A] border-white/10">
                              {venuesList.map((venue) => (
                                <SelectItem key={venue.id} value={venue.id}>
                                  <div className="flex items-center gap-2">
                                    <MapPin className="w-3 h-3 text-[#34C759]" />
                                    <span>{venue.name}</span>
                                    <span className="text-[#71717A] text-xs">- {venue.city}</span>
                                  </div>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        
                        {selectedVenueId && (
                          <div className="p-3 bg-[#0A0A0A] border border-white/[0.05] rounded-sm">
                            <p className="text-xs text-[#71717A] mb-2">Se guardarán:</p>
                            <div className="flex flex-wrap gap-2">
                              {result?.assigned_frequencies?.slice(0, 6).map((freq, idx) => (
                                <span key={idx} className="px-2 py-1 bg-[#34C759]/10 text-[#34C759] text-xs font-mono rounded">
                                  {freq.frequency.toFixed(3)} MHz
                                </span>
                              ))}
                              {result?.assigned_frequencies?.length > 6 && (
                                <span className="px-2 py-1 bg-white/5 text-[#71717A] text-xs rounded">
                                  +{result.assigned_frequencies.length - 6} más
                                </span>
                              )}
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                ) : (
                  // Create new venue form
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="form-group">
                        <Label className="form-label">
                          Nombre del Venue <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          value={newVenueForm.name}
                          onChange={(e) => setNewVenueForm({ ...newVenueForm, name: e.target.value })}
                          placeholder="Ej: Teatro Municipal"
                          className="input-tactical"
                          data-testid="new-venue-name"
                        />
                      </div>
                      <div className="form-group">
                        <Label className="form-label">
                          Ciudad <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          value={newVenueForm.city}
                          onChange={(e) => setNewVenueForm({ ...newVenueForm, city: e.target.value })}
                          placeholder="Ej: Santiago"
                          className="input-tactical"
                          data-testid="new-venue-city"
                        />
                      </div>
                    </div>
                    
                    <div className="form-group">
                      <Label className="form-label">Dirección (opcional)</Label>
                      <Input
                        value={newVenueForm.address}
                        onChange={(e) => setNewVenueForm({ ...newVenueForm, address: e.target.value })}
                        placeholder="Ej: Av. Principal 123"
                        className="input-tactical"
                        data-testid="new-venue-address"
                      />
                    </div>
                    
                    <div className="form-group">
                      <Label className="form-label">Notas (opcional)</Label>
                      <Textarea
                        value={newVenueForm.notes}
                        onChange={(e) => setNewVenueForm({ ...newVenueForm, notes: e.target.value })}
                        placeholder="Notas sobre RF del venue, interferencias conocidas, etc..."
                        className="input-tactical min-h-[60px] resize-none"
                        data-testid="new-venue-notes"
                      />
                    </div>

                    {/* Equipment info badge */}
                    {selectedEquipment && (
                      <div className="p-3 bg-[#34C759]/10 border border-[#34C759]/30 rounded-sm">
                        <div className="flex items-center gap-2">
                          <Cpu className="w-4 h-4 text-[#34C759]" />
                          <span className="text-sm text-[#34C759]">
                            Equipo: {selectedEquipment.full_name} • {result?.total_channels} frecuencias
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowVenueDialog(false)}
              className="border-gray-600 text-gray-300"
            >
              Cancelar
            </Button>
            <Button
              onClick={showNewVenueForm ? handleCreateVenueAndSave : handleSaveToExistingVenue}
              disabled={savingToVenue || (!showNewVenueForm && !selectedVenueId) || (showNewVenueForm && (!newVenueForm.name || !newVenueForm.city))}
              className="bg-[#34C759] hover:bg-[#34C759]/90 text-black"
              data-testid="venue-save-btn"
            >
              {savingToVenue ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Guardando...
                </>
              ) : (
                <>
                  <MapPin className="w-4 h-4 mr-2" />
                  {showNewVenueForm ? "Crear y Guardar" : "Guardar Frecuencias"}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* AI Analysis Dialog */}
      <AIAnalysisDialog
        open={showAIAnalysis}
        onOpenChange={setShowAIAnalysis}
        frequencies={result?.assigned_frequencies?.map(f => f.frequency) || []}
        equipmentName={selectedEquipment?.full_name}
        venueName={null}
        eventType="Evento en vivo"
      />

      {/* Upgrade Modal */}
      <UpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        feature="systems"
        currentPlan={subscriptionUsage?.plan_name || "Gratuito"}
        limit={subscriptionUsage?.usage?.systems?.limit}
        current={subscriptionUsage?.usage?.systems?.current}
      />

      {/* Admin: Add Equipment Dialog */}
      <Dialog open={showAddEquipmentDialog} onOpenChange={setShowAddEquipmentDialog}>
        <DialogContent className="bg-[#111111] border-white/10 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-[#F4F4F5] flex items-center gap-2">
              <Settings className="w-5 h-5 text-[#34C759]" />
              Agregar Nuevo Equipo
            </DialogTitle>
            <DialogDescription className="text-[#71717A]">
              Agrega un equipo IEM o micrófono con todas sus especificaciones y frecuencias preset.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[#A1A1AA]">Marca *</Label>
                <Input
                  value={newEquipmentForm.brand}
                  onChange={(e) => setNewEquipmentForm({ ...newEquipmentForm, brand: e.target.value })}
                  placeholder="Ej: Sennheiser"
                  className="input-tactical mt-1"
                />
              </div>
              <div>
                <Label className="text-[#A1A1AA]">Modelo *</Label>
                <Input
                  value={newEquipmentForm.model}
                  onChange={(e) => setNewEquipmentForm({ ...newEquipmentForm, model: e.target.value })}
                  placeholder="Ej: EW-D EM"
                  className="input-tactical mt-1"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-[#A1A1AA]">Nombre de Banda</Label>
                <Input
                  value={newEquipmentForm.band_name}
                  onChange={(e) => setNewEquipmentForm({ ...newEquipmentForm, band_name: e.target.value })}
                  placeholder="Ej: Aw, L51"
                  className="input-tactical mt-1"
                />
              </div>
              <div>
                <Label className="text-[#A1A1AA]">Freq. Mín (MHz) *</Label>
                <Input
                  type="number"
                  step="0.001"
                  value={newEquipmentForm.band_min}
                  onChange={(e) => setNewEquipmentForm({ ...newEquipmentForm, band_min: e.target.value })}
                  placeholder="470.000"
                  className="input-tactical mt-1 font-mono"
                />
              </div>
              <div>
                <Label className="text-[#A1A1AA]">Freq. Máx (MHz) *</Label>
                <Input
                  type="number"
                  step="0.001"
                  value={newEquipmentForm.band_max}
                  onChange={(e) => setNewEquipmentForm({ ...newEquipmentForm, band_max: e.target.value })}
                  placeholder="698.000"
                  className="input-tactical mt-1 font-mono"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-[#A1A1AA]">Tipo de Sistema</Label>
                <Select
                  value={newEquipmentForm.system_type}
                  onValueChange={(value) => setNewEquipmentForm({ ...newEquipmentForm, system_type: value })}
                >
                  <SelectTrigger className="input-tactical mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1A1A1A] border-white/10">
                    <SelectItem value="iem">In-Ear Monitor (IEM)</SelectItem>
                    <SelectItem value="microphone">Micrófono Inalámbrico</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[#A1A1AA]">Máx. Sistemas</Label>
                <Input
                  type="number"
                  value={newEquipmentForm.max_systems}
                  onChange={(e) => setNewEquipmentForm({ ...newEquipmentForm, max_systems: e.target.value })}
                  placeholder="16"
                  className="input-tactical mt-1"
                />
              </div>
              <div>
                <Label className="text-[#A1A1AA]">Canales/Grupo</Label>
                <Input
                  type="number"
                  value={newEquipmentForm.channels_per_group}
                  onChange={(e) => setNewEquipmentForm({ ...newEquipmentForm, channels_per_group: e.target.value })}
                  placeholder="6"
                  className="input-tactical mt-1"
                />
              </div>
            </div>
            
            {/* Specifications Section */}
            <div className="border border-white/10 rounded-sm p-3 space-y-3">
              <div className="flex items-center gap-2 text-sm font-medium text-[#A1A1AA]">
                <Cpu className="w-4 h-4 text-[#34C759]" />
                Especificaciones Técnicas
              </div>
              <div className="grid grid-cols-5 gap-2">
                <div>
                  <Label className="text-xs text-[#71717A]">Espaciado Min</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={newEquipmentForm.specifications.min_spacing_mhz}
                    onChange={(e) => setNewEquipmentForm({ 
                      ...newEquipmentForm, 
                      specifications: { ...newEquipmentForm.specifications, min_spacing_mhz: e.target.value }
                    })}
                    placeholder="0.5"
                    className="input-tactical mt-1 font-mono text-xs"
                  />
                </div>
                <div>
                  <Label className="text-xs text-[#71717A]">Step Size</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={newEquipmentForm.specifications.step_size_mhz}
                    onChange={(e) => setNewEquipmentForm({ 
                      ...newEquipmentForm, 
                      specifications: { ...newEquipmentForm.specifications, step_size_mhz: e.target.value }
                    })}
                    placeholder="0.5"
                    className="input-tactical mt-1 font-mono text-xs"
                  />
                </div>
                <div>
                  <Label className="text-xs text-[#71717A]">SNR (dB)</Label>
                  <Input
                    type="number"
                    value={newEquipmentForm.specifications.snr_db}
                    onChange={(e) => setNewEquipmentForm({ 
                      ...newEquipmentForm, 
                      specifications: { ...newEquipmentForm.specifications, snr_db: e.target.value }
                    })}
                    placeholder="90"
                    className="input-tactical mt-1 font-mono text-xs"
                  />
                </div>
                <div>
                  <Label className="text-xs text-[#71717A]">Potencia mW</Label>
                  <Input
                    value={newEquipmentForm.specifications.power_mw}
                    onChange={(e) => setNewEquipmentForm({ 
                      ...newEquipmentForm, 
                      specifications: { ...newEquipmentForm.specifications, power_mw: e.target.value }
                    })}
                    placeholder="10/30"
                    className="input-tactical mt-1 font-mono text-xs"
                  />
                </div>
                <div>
                  <Label className="text-xs text-[#71717A]">Modulación</Label>
                  <Input
                    value={newEquipmentForm.specifications.modulation}
                    onChange={(e) => setNewEquipmentForm({ 
                      ...newEquipmentForm, 
                      specifications: { ...newEquipmentForm.specifications, modulation: e.target.value }
                    })}
                    placeholder="FM Estéreo"
                    className="input-tactical mt-1 text-xs"
                  />
                </div>
              </div>
            </div>
            
            {/* Notes Section */}
            <div>
              <Label className="text-[#A1A1AA]">Notas y Observaciones</Label>
              <textarea
                value={newEquipmentForm.notes}
                onChange={(e) => setNewEquipmentForm({ ...newEquipmentForm, notes: e.target.value })}
                placeholder="Ej: Sistema económico ideal para iglesias. No usar pilas de litio..."
                className="w-full mt-1 p-2 bg-[#0A0A0A] border border-white/10 rounded-sm text-[#F4F4F5] text-sm resize-none h-16"
              />
            </div>
            
            {/* Preset Frequencies Section */}
            <div className="border border-white/10 rounded-sm p-3 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm font-medium text-[#A1A1AA]">
                  <Radio className="w-4 h-4 text-[#34C759]" />
                  Frecuencias Preset por Grupo
                </div>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => setShowPresetEditor(!showPresetEditor)}
                  className="text-xs border-[#34C759]/30 text-[#34C759] hover:bg-[#34C759]/10"
                >
                  {showPresetEditor ? "Ocultar Editor" : "Agregar Frecuencias"}
                </Button>
              </div>
              
              {/* Show configured groups */}
              {Object.keys(newEquipmentForm.preset_frequencies).length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {Object.entries(newEquipmentForm.preset_frequencies).map(([group, freqs]) => (
                    <div 
                      key={group} 
                      className="flex items-center gap-1 px-2 py-1 bg-[#34C759]/10 border border-[#34C759]/30 rounded-sm text-xs"
                    >
                      <span className="text-[#34C759] font-mono font-medium">
                        Grupo {group}: {Object.keys(freqs).length} freq
                      </span>
                      <button
                        onClick={() => handleLoadPresetGroup(group)}
                        className="text-[#71717A] hover:text-white ml-1"
                        title="Editar"
                      >
                        ✏️
                      </button>
                      <button
                        onClick={() => handleDeletePresetGroup(group)}
                        className="text-red-400 hover:text-red-300"
                        title="Eliminar"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Preset Editor */}
              {showPresetEditor && (
                <div className="space-y-3 pt-2 border-t border-white/10">
                  <div className="flex items-center gap-3">
                    <Label className="text-xs text-[#71717A] w-16">Grupo:</Label>
                    <select 
                      value={currentPresetGroup} 
                      onChange={(e) => handleLoadPresetGroup(e.target.value)}
                      className="w-20 h-8 text-xs bg-[#0A0A0A] border border-white/10 rounded px-2 text-white focus:border-[#34C759] focus:outline-none"
                    >
                      {["A","B","C","D","E","F","G","H","I","J","K","L","M","N","O","P","Q","R","S","T"].map(g => (
                        <option key={g} value={g}>{g}</option>
                      ))}
                    </select>
                    <span className="text-xs text-[#71717A]">
                      (o usa 1-10 para Sennheiser)
                    </span>
                  </div>
                  
                  <div className="grid grid-cols-6 gap-2">
                    {[1, 2, 3, 4, 5, 6].map(ch => (
                      <div key={ch}>
                        <Label className="text-xs text-[#71717A]">Ch {ch}</Label>
                        <Input
                          type="number"
                          step="0.001"
                          value={presetGroupFrequencies[ch] || ""}
                          onChange={(e) => setPresetGroupFrequencies(prev => ({ ...prev, [ch]: e.target.value }))}
                          placeholder={`${560 + (ch * 6)}.000`}
                          className="input-tactical mt-1 font-mono text-xs h-8"
                        />
                      </div>
                    ))}
                  </div>
                  
                  <Button
                    type="button"
                    size="sm"
                    onClick={handleSavePresetGroup}
                    className="w-full bg-[#34C759]/20 hover:bg-[#34C759]/30 text-[#34C759] border border-[#34C759]/30"
                  >
                    <Plus className="w-3 h-3 mr-1" />
                    Guardar Grupo {currentPresetGroup}
                  </Button>
                  
                  <p className="text-xs text-[#71717A]">
                    Puedes agregar múltiples grupos. Cada grupo tendrá sus frecuencias preset que se mostrarán en el dropdown de sistemas.
                  </p>
                </div>
              )}
            </div>
          </div>
          
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => {
                setShowAddEquipmentDialog(false);
                setShowPresetEditor(false);
              }}
              className="text-[#71717A]"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleAddEquipment}
              disabled={addingEquipment}
              className="bg-[#34C759] hover:bg-[#34C759]/90 text-black"
            >
              {addingEquipment ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Agregando...
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4 mr-2" />
                  Agregar Equipo
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
